import React, { FC, useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
  LegislationContextType,
  LegislationArticlesNav,
  LegislationInfos,
  LegislationAllInfosType,
} from '../legislation.types';

import { getLegislationInfos } from '../legislation.api';
import { useLinkedDecisionsFetcher } from './page/hooks/useLinkedDecisionsFetcher';
import { useLinkedLegislationsFetcher } from './page/hooks/useLinkedLegislationsFetcher';
import { useSearchWithinRestrictedArticles } from './page/hooks/useSearchWithinRestrictedArticles';

export const LegislationContext = React.createContext<LegislationContextType>({
  articleNumber: null,
  legislationTitle: '',
  fullTitle: '',
  legislationInfos: null,
  legislationInfosAll: [],
  legislationInfosPreviousAndNext: {
    legislationInfosPrevious: null,
    legislationInfosNext: null,
  },
  initialVersionId: '',
  allVersionsIds: [],
  setArticleId: () => {},
  articleId: '',
  commentaries: { data: null, filters: null },
  decisions: { data: null, filters: null },
  preparatoryWork: { data: null, filters: null },
  referencesDecisionsIds: [],
  QPCDecisionsIds: [],
  quotedByLegislations: null,
  textNature: '',
  isCode: false,
  textId: '',
});

export const LegislationProvider: FC<{
  legislationInfosAll: LegislationAllInfosType;
}> = ({ children, legislationInfosAll }) => {
  const [
    legislationInfosPreviousAndNext,
    setLegislationInfosPreviousAndNext,
  ] = useState<LegislationArticlesNav>({
    legislationInfosPrevious: null,
    legislationInfosNext: null,
  });
  const [articleId, setArticleId] = useState(legislationInfosAll.articleId);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const queryParam = searchParams.get('q') || '';

  useEffect(() => {
    const setPreviousNextArticle = async (id: string) => {
      try {
        const legislationInfosLatest = await getLegislationInfos({
          articleId: id,
          query: queryParam,
        });
        const [
          legislationInfosPreviousFetched,
          legislationInfosNextFetched,
        ] = await Promise.all([
          getLegislationInfos({
            articleId: legislationInfosLatest.previousArticleId,
            query: queryParam,
          }),
          getLegislationInfos({
            articleId: legislationInfosLatest.nextArticleId,
            query: queryParam,
          }),
        ]);

        setLegislationInfosPreviousAndNext({
          legislationInfosPrevious: legislationInfosPreviousFetched,
          legislationInfosNext: legislationInfosNextFetched,
        });
      } catch (error) {
        throw error;
      }
    };

    const setInitialData = () => {
      const isArticlePresent = legislationInfosAll.versions.some(
        (version: LegislationInfos) => version.articleId === articleId,
      );
      if (
        legislationInfosAll.versions &&
        legislationInfosAll.versions.length > 0 &&
        isArticlePresent
      ) {
        setLegislationInfosPreviousAndNext({
          legislationInfosPrevious: null,
          legislationInfosNext: null,
        });
      }
      setPreviousNextArticle(articleId);
    };

    setInitialData();
  }, [articleId]);

  const {
    articleNumber,
    legislationTitle,
    fullTitle,
    versions,
    initialVersionId,
    isCode,
    textId,
    textNature,
  } = legislationInfosAll;

  const linkedDecisions = useLinkedDecisionsFetcher(articleId);
  const quotedByLegislations = useLinkedLegislationsFetcher(
    articleId,
    'quoted_by',
    4,
    true,
  );

  const legislationInfos =
    versions.find(
      (legislation: LegislationInfos) => legislation.articleId === articleId,
    ) || null;

  const allVersionsIds = useMemo(() => versions.map((v) => v.articleId), [
    versions,
  ]);

  const referencesDecisionsIds = useMemo(
    () => linkedDecisions.referenceDecisions.map(({ docId }) => docId),
    [linkedDecisions.referenceDecisions],
  );

  const QPCDecisionsIds = useMemo(
    () => linkedDecisions.qpcDecisions.map(({ docId }) => docId),
    [linkedDecisions.qpcDecisions],
  );

  const commentaries = useSearchWithinRestrictedArticles(
    {
      type: 'commentary',
      restrict_to_law_article_ids: allVersionsIds,
      chrono: true,
      size: 8,
    },
    initialVersionId,
  );
  const decisions = useSearchWithinRestrictedArticles({
    type: 'arret',
    restrict_to_law_article_ids: allVersionsIds,
    size: 6,
  });
  const preparatoryWork = useSearchWithinRestrictedArticles({
    type: 'preparatory_work',
    restrict_to_law_article_ids: allVersionsIds,
    size: 6,
  });

  return (
    <LegislationContext.Provider
      value={{
        articleNumber,
        legislationTitle,
        fullTitle,
        legislationInfos,
        legislationInfosAll: versions,
        legislationInfosPreviousAndNext,
        initialVersionId,
        allVersionsIds,
        isCode,
        textId,
        setArticleId,
        articleId,
        commentaries,
        decisions,
        preparatoryWork,
        referencesDecisionsIds,
        QPCDecisionsIds,
        quotedByLegislations,
        textNature,
      }}
    >
      {children}
    </LegislationContext.Provider>
  );
};
